import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SsrContext } from './ssr-context';
import { ChevronDownIcon, ChevronUpIcon, CopyIcon, PlusIcon, TrashIcon } from '../icons';
import type { PageContent, PageTheme, Section, PageStyle, SectionAnimation } from '../model/pages';
import { normalizeContent, thankYouSections, afterSubmitAction, visitorSections, SECTION_LABEL } from '../model/pages';
import { mergeSection } from '../model/merge-fields';
import { styleFor, bandTone, isModernBg, darkMark, luminance, INK_TONE, type StyleClasses } from './styles';
import { sectionGap, bandPad } from './spacing';
import { fontFor } from './fonts';
import SectionRenderer, { type PageFormLink } from './SectionRenderer';
import FormModal from './FormModal';
import type { FormDone } from './CrmFormFrame';
import type { PreviewForm } from './FormMock';
import { EditorialBackdrop, EditorialMotif } from './EditorialDecor';
import { normalizeSectionDesign } from '../model/section-design';
import { SECTION_MAX_WIDTH, sectionDesignPresentation } from './section-design';

/** Window event that opens the popup form (theme.form_mode 'popup'): the public
 *  page dispatches it when a last-step '#next' falls back to the form. */
export const OPEN_FORM_EVENT = 'nla:open-form';

/** "Try it" mode on the editor canvas: the page works the way a visitor's does
 *  (buttons, steps, FAQ, popups, the form in preview mode), but nothing is
 *  saved and no link leaves the editor. Only the editor canvas ever passes this. */
export interface TryItOptions {
  /** One plain sentence about what happened instead of the real thing ("Nothing
   *  was saved"). The editor shows it as a toast. */
  onNotice: (message: string) => void;
  /** Go to the next step: a '#next' button, or a form set to move on. */
  onNextStep?: () => void;
  /** Show this page's thank-you, like a real submit on the last step. */
  onThankYou?: () => void;
  /** The canvas shows the funnel's last step, so '#next' falls back to the
   *  form, like the live page. Absent: the last step when there is no
   *  onNextStep. */
  lastStep?: boolean;
}

/** What Try it says in place of a real action. */
export const TRY_IT_NOTICE = {
  form: 'That’s how the form behaves. Nothing was saved.',
  link: 'Links open in Preview. Use the eye button.',
  booking: 'The calendar works in Preview. Use the eye button.',
  embed: 'Embeds work in Preview. Use the eye button.',
} as const;

interface Props {
  content: PageContent;
  theme: PageTheme;
  /** The page's CRM form: a live frame of /f/<slug> in every Form section and
   *  the popup. None: galleries show `previewForm`, the edit canvas says what to
   *  do, a public page shows no form card. */
  form?: PageFormLink | null;
  /** The page id, sent with each lead (?pg=) so it counts for this page. */
  pageId?: string;
  /** Galleries and template previews with no real form: a still of the form
   *  the template will create (FormMock). */
  previewForm?: PreviewForm | null;
  /** Open the form in preview mode (preview=1, staff answers never saved). The
   *  public page sets it on ?pt= previews; the editor canvas always does. */
  formPreview?: boolean;
  /** Public page: a form was sent (drives the funnel, see PublicLandingPage). */
  onFormDone?: (done: FormDone) => void;
  /** Public page: a call was booked (a form's calendar or a booking section). */
  onBooked?: () => void;
  /** A read-only render (thumbnails, galleries): videos stay stills and the
   *  form frame takes no clicks. */
  preview?: boolean;
  showThankYou?: boolean;
  /** Optional pre-normalized sections (editor passes live sections so ids stay stable). */
  sections?: Section[];
  /** When set, {{tokens}} in every section are filled from this context (booking/form handoff). */
  merge?: Record<string, string>;
  /** Public page only: fired when a CTA is clicked, for analytics. */
  onCtaClick?: (label: string) => void;
  /** Multi-step pages: a CTA with href '#next' calls this instead of navigating. */
  onNextStep?: () => void;
  // ── Editor-only: visual click-to-edit layer ──────────────────────────
  /** Wraps every rendered section (incl. the shared footer) in a click-to-select
   *  layer: a hover outline and type chip, a selected outline with a section
   *  toolbar and an add-below button. Also suppresses the Reveal scroll
   *  animation, real link navigation, the form, and '#next' advancing.
   *  When falsy (every public page) the render tree is EXACTLY the same as
   *  before — no wrappers, no data attributes, no handlers. */
  editable?: boolean;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  /** Editable mode: a click on the page background (anywhere that is not inside
   *  a section) calls this, so the editor can clear the selection. */
  onDeselect?: () => void;
  onMove?: (id: string, dir: -1 | 1) => void;
  /** Editable mode: move a section to `toIndex` in the FULL section list
   *  (hidden sections included). The toolbar's Up and Down use it when hidden
   *  sections sit between a section and the visible neighbor it swaps with;
   *  otherwise (and when this is absent) they call onMove. */
  onMoveTo?: (id: string, toIndex: number) => void;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
  /** The blue "+" on the selected section's bottom edge. */
  onAddBelow?: (id: string) => void;
  /** Inline text editing (Visual canvas): every section's text fields render as
   *  inline editors and commit field patches here on blur. */
  onEditText?: (id: string, patch: Partial<Section>) => void;
  /** True while a drag session is active (a library component or a canvas section
   *  is being dragged). Gates the canvas drop targets so unrelated drags are ignored. */
  canvasDragging?: boolean;
  /** A drag was dropped on the canvas. `beforeId` = the section to insert before
   *  (null = append). The parent reads its own drag payload to add vs. reorder. */
  onCanvasDrop?: (beforeId: string | null) => void;
  /** A canvas section started being dragged (to reorder) — parent records it as the
   *  drag payload. */
  onCanvasDragStart?: (id: string) => void;
  /** Editor "Phone" preview: force the two-column section layouts to stack
   *  (passed through to SectionRenderer). Absent on public pages. */
  stack?: boolean;
  /** Editor canvas: the sections the last change touched glow once. `flashKey`
   *  changes with every change, so a section can glow again. Never passed by
   *  a public page. */
  flashIds?: string[];
  flashKey?: number;
  /** Editor canvas, "Try it" mode (see TryItOptions). Wins over `editable`: no
   *  edit chrome and no inline editors while it is set. Never passed by a
   *  public page. */
  interactive?: TryItOptions;
  /** View-only editor canvas: sections can still be selected (to read their
   *  settings) but there is no section toolbar, no "+" and no dragging. */
  readOnly?: boolean;
}

/** Fades a section up as it scrolls into view. No-ops (renders visible) when
 *  IntersectionObserver is missing or the user prefers reduced motion.
 *  `immediate` renders it already-shown and skips the observer entirely — for
 *  the first section (hero), which is above the fold on load and shouldn't
 *  wait on a fade; it should just paint. */
// Hidden-state transform per animation variant. Absent/'fade-up' is the transform
// every existing page already had; 'fade-in' animates opacity only.
const REVEAL_FROM: Record<Exclude<SectionAnimation, 'none'>, string> = {
  'fade-up': 'translateY(16px)',
  'fade-in': 'none',
  'slide-left': 'translateX(-24px)',
  'slide-right': 'translateX(24px)',
  'zoom': 'scale(0.96)',
};

function Reveal({ children, immediate, animation }: { children: React.ReactNode; immediate?: boolean; animation?: SectionAnimation }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const off = animation === 'none';
  // A server-rendered page (SsrContext) starts every section visible, because
  // that is what its HTML already painted. Right after hydration, sections that
  // are still entirely below the fold are hidden again, with no transition (they
  // are off screen), so they keep their scroll fade.
  const ssr = useContext(SsrContext);
  const [shown, setShown] = useState(ssr || off || immediate || reduced || typeof IntersectionObserver === 'undefined');
  const [rearmed, setRearmed] = useState(false);

  useEffect(() => {
    if (!ssr || off || immediate || reduced || typeof IntersectionObserver === 'undefined') return;
    const el = ref.current;
    if (el && el.getBoundingClientRect().top >= window.innerHeight) { setRearmed(true); setShown(false); }
    // Mount only: the decision is about where the section sat when the page woke up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shown || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setShown(true); io.disconnect(); }
    }, { rootMargin: '0px 0px -10% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [shown]);

  // 'none' means exactly that: no wrapper styles at all, so nothing can flash.
  if (off) return <div ref={ref}>{children}</div>;

  return (
    <div ref={ref} style={{
      transition: rearmed && !shown ? 'none' : 'opacity .6s ease, transform .6s ease',
      opacity: shown ? 1 : 0,
      transform: shown ? 'none' : REVEAL_FROM[animation ?? 'fade-up'],
    }}>{children}</div>
  );
}

// ── Canvas chrome ───────────────────────────────────────────────────────
// Everything below renders INSIDE the page frame, where the app's tokens,
// type scale and dark mode do not reach. So it uses fixed px sizes and fixed
// colors (Flowmark's), and sets its own type, so no page style can restyle it.

const BLUE = '#0071e3';
const CHROME_TYPE: React.CSSProperties = {
  font: "500 12px/1 -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  letterSpacing: 0,
  textTransform: 'none',
  whiteSpace: 'nowrap',
};

/** One 28px button in the section toolbar. */
function BarBtn({ label, onClick, disabled, danger, children }: {
  label: string; onClick: () => void; disabled?: boolean; danger?: boolean; children: React.ReactNode;
}) {
  return (
    <button type="button" aria-label={label} title={label} disabled={disabled} onClick={onClick}
      className={`grid h-[28px] w-[28px] shrink-0 place-items-center rounded-[5px] border-0 bg-transparent p-0 text-[#404040] transition-colors duration-[120ms] focus-visible:rounded-[5px] disabled:pointer-events-none disabled:opacity-30 ${
        danger ? 'hover:bg-[#fdeded] hover:text-[#c50006]' : 'hover:bg-[#f2f2f2] hover:text-[#171717]'
      }`}>{children}</button>
  );
}

/** How to scroll a section selected from outside the canvas (the outline, add,
 *  duplicate) into view: to the center when more than half of it is out of
 *  view (its top, when it is taller than the frame), else the least scroll
 *  that shows it. */
function scrollBlockFor(rect: { top: number; bottom: number; height: number }, viewHeight: number): ScrollLogicalPosition {
  if (!viewHeight || rect.height <= 0) return 'nearest';
  const visible = Math.max(0, Math.min(rect.bottom, viewHeight) - Math.max(rect.top, 0));
  if (visible * 2 >= rect.height) return 'nearest';
  return rect.height > viewHeight ? 'start' : 'center';
}

/** Editor-canvas wrapper around one rendered section (Flowmark's canvas chrome):
 *  - Hover: a 1px blue outline on the section's edge and a type chip inside
 *    its top-left corner.
 *  - Selected: a 2px blue outline, no chip, the section toolbar inside the
 *    top-right corner (it stays in view while the section is taller than the
 *    frame, and stops at the section's bottom), and a blue "+" centered on the
 *    bottom edge that adds a section below.
 *  - A click selects the section instead of doing its real job (navigating,
 *    submitting, advancing a step). A click on text goes straight into typing
 *    there, and selects the section through focus.
 *  Everything is drawn inside the section's own box with absolute positioning,
 *  so hovering or selecting never moves the page. Never rendered on public
 *  pages. */
function EditableSection({ id, label, selected, isFooter, canUp, canDown, dropEdge, draggable, dndActive, skipInitialScroll, flashKey,
  readOnly, onDragStartSelf, onDragOverSelf, onDropSelf, onSelect, onMoveBy, onDuplicate, onDelete, onAddBelow, children }: {
  id: string; label: string; selected: boolean; isFooter: boolean; canUp: boolean; canDown: boolean;
  /** View-only canvas: selecting outlines the section, with no toolbar or "+". */
  readOnly?: boolean;
  /** The last change touched this section: it glows once (a new key glows again). */
  flashKey?: number;
  dropEdge?: 'above' | 'below' | null;
  draggable?: boolean;            // can be dragged to reorder (not while its text is being edited)
  dndActive?: boolean;            // a drag session is in progress → act as a drop target
  /** Mounting selected must not scroll the canvas (switching back from Try it). */
  skipInitialScroll?: boolean;
  onDragStartSelf?: () => void;
  onDragOverSelf?: (below: boolean) => void;
  onDropSelf?: (below: boolean) => void;
  onSelect?: (id: string) => void; onMoveBy?: (dir: -1 | 1) => void; onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void; onAddBelow?: (id: string) => void; children: React.ReactNode;
}) {
  const halfBelow = (e: React.DragEvent): boolean => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return e.clientY > r.top + r.height / 2;
  };
  const ref = useRef<HTMLDivElement>(null);
  // Set when this section is selected by a click on it: the user is already
  // looking at it, so the canvas must not scroll out from under the pointer.
  const clicked = useRef(!!skipInitialScroll);
  // The last press started on the toolbar or the "+": a drag from there must
  // not pick up the whole section.
  const fromChrome = useRef(false);
  // Inline text is being edited: the section stops being draggable so a drag
  // selects text instead of picking up the whole section.
  const [typing, setTyping] = useState(false);
  // Selecting from outside the canvas (outline, add, duplicate) brings the
  // section into view: centered when it is mostly hidden, else the least scroll.
  useEffect(() => {
    if (!selected) { clicked.current = false; return; }
    if (clicked.current) { clicked.current = false; return; }
    const el = ref.current;
    if (!el || typeof el.scrollIntoView !== 'function') return;
    const view = el.ownerDocument.defaultView;
    const reduce = !!view?.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: scrollBlockFor(el.getBoundingClientRect(), view?.innerHeight ?? 0) });
  }, [selected]);
  const inText = (t: EventTarget) => !!(t as HTMLElement).closest?.('[data-edit-text]');
  const inChrome = (t: EventTarget) => !!(t as HTMLElement).closest?.('[data-edit-toolbar]');
  const canDrag = !!draggable && !typing;
  return (
    <div ref={ref} data-section-id={id}
      // Selected sits above its neighbors, so the next section never covers the
      // "+" on its bottom edge. Never overflow-hidden: the "+" hangs below.
      // Nested frames (a booking calendar, an embed, a video) do not take
      // clicks on the canvas: a click there selects the section.
      className={`group/edit relative cursor-default [&_iframe]:pointer-events-none ${selected ? 'z-[2]' : 'z-0'}`}
      style={{ scrollMarginTop: 8, scrollMarginBottom: 16 }}
      draggable={canDrag}
      onMouseDownCapture={(e) => { fromChrome.current = inChrome(e.target); }}
      // One click to edit: focusing any text field selects its section (no
      // second click), without scrolling the canvas.
      onFocusCapture={(e) => {
        if (!inText(e.target)) return;
        setTyping(true);
        if (!selected) { clicked.current = true; onSelect?.(id); }
      }}
      onBlurCapture={(e) => { if (inText(e.target)) setTyping(false); }}
      onDragStart={canDrag ? (e) => {
        if (fromChrome.current) { e.preventDefault(); return; }
        try { e.dataTransfer.setData('text/plain', id); e.dataTransfer.effectAllowed = 'move'; } catch { /* noop */ }
        onDragStartSelf?.();
      } : undefined}
      onDragOver={dndActive ? (e) => { e.preventDefault(); onDragOverSelf?.(halfBelow(e)); } : undefined}
      onDrop={dndActive ? (e) => { e.preventDefault(); e.stopPropagation(); onDropSelf?.(halfBelow(e)); } : undefined}
      // Capture phase: any click inside the section selects it instead of doing
      // its real job (links, '#next' CTAs, FAQ toggles). The toolbar and the "+"
      // run their own handlers; a click on text is the text editor's (it
      // suppresses the click's default itself, and focus selected the section).
      onClickCapture={(e) => {
        const t = e.target as HTMLElement;
        if (inChrome(t) || inText(t)) return;
        e.preventDefault();
        e.stopPropagation();
        if (!selected) clicked.current = true;
        onSelect?.(id);
      }}
      onSubmitCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      {children}
      {flashKey !== undefined && (
        // The change glow: a soft blue wash that fades once.
        <div key={flashKey} aria-hidden data-edit-flash
          className="pointer-events-none absolute inset-0 z-[19] animate-[pe-flash_900ms_ease-out_forwards] motion-reduce:hidden"
          style={{ background: 'rgba(0,113,227,.10)' }} />
      )}
      <div aria-hidden data-edit-outline
        className={`pointer-events-none absolute inset-0 z-[20] transition-opacity duration-[120ms] ease-out motion-reduce:transition-none ${selected ? '' : 'opacity-0 group-hover/edit:opacity-100'}`}
        style={{ boxShadow: `inset 0 0 0 ${selected ? 2 : 1}px ${BLUE}` }} />
      {!selected && (
        <span aria-hidden data-edit-tag
          className="pointer-events-none absolute left-[6px] top-[6px] z-[21] flex h-[20px] items-center rounded-[4px] px-[7px] opacity-0 transition-opacity duration-[120ms] ease-out group-hover/edit:opacity-100 motion-reduce:transition-none"
          style={{ ...CHROME_TYPE, background: BLUE, color: '#ffffff' }}>
          {label}
        </span>
      )}
      {selected && !readOnly && (
        // A pointer-transparent column down the section's right side: the bar
        // inside sticks 8px below the top of the frame while the section
        // scrolls, and stops at the section's bottom.
        <div className="pointer-events-none absolute inset-y-0 right-[8px] z-[22] flex flex-col items-end">
          <div role="toolbar" aria-label="Section actions" data-edit-toolbar
            className="pointer-events-auto sticky top-[8px] mt-[8px] flex origin-top-right animate-[pe-rise_160ms_cubic-bezier(.2,0,0,1)] items-center gap-[2px] rounded-[7px] bg-white p-[3px] motion-reduce:animate-none"
            style={{ ...CHROME_TYPE, color: '#404040', boxShadow: '0 0 0 1px rgba(0,0,0,.08), 0 6px 18px rgba(0,0,0,.14)' }}>
            {isFooter ? (
              <BarBtn label="Remove footer" danger onClick={() => onDelete?.(id)}><TrashIcon size={15} strokeWidth={2} /></BarBtn>
            ) : (
              <>
                <BarBtn label="Move section up" disabled={!canUp} onClick={() => onMoveBy?.(-1)}><ChevronUpIcon size={15} strokeWidth={2} /></BarBtn>
                <BarBtn label="Move section down" disabled={!canDown} onClick={() => onMoveBy?.(1)}><ChevronDownIcon size={15} strokeWidth={2} /></BarBtn>
                <span aria-hidden data-edit-sep className="mx-[2px] h-[20px] w-px shrink-0 bg-[#ebebeb]" />
                <BarBtn label="Duplicate section" onClick={() => onDuplicate?.(id)}><CopyIcon size={15} strokeWidth={2} /></BarBtn>
                <BarBtn label="Delete section" danger onClick={() => onDelete?.(id)}><TrashIcon size={15} strokeWidth={2} /></BarBtn>
              </>
            )}
          </div>
        </div>
      )}
      {selected && !readOnly && !isFooter && onAddBelow && (
        <button type="button" data-edit-toolbar data-edit-add aria-label="Add a section below" title="Add a section below"
          className="absolute -bottom-[13px] left-1/2 z-[23] grid h-[26px] w-[26px] -translate-x-1/2 animate-[pe-plus_200ms_cubic-bezier(.2,0,0,1)] place-items-center rounded-full border-0 bg-[#0071e3] p-0 text-white transition-colors duration-[120ms] hover:bg-[#005bb8] focus-visible:rounded-full motion-reduce:animate-none"
          style={{ ...CHROME_TYPE, color: '#ffffff', boxShadow: '0 0 0 3px #ffffff, 0 4px 12px rgba(0,0,0,.18)' }}
          onClick={() => onAddBelow(id)}>
          <PlusIcon size={15} strokeWidth={2} />
        </button>
      )}
      {dropEdge && (
        <div aria-hidden data-edit-drop
          className={`pointer-events-none absolute inset-x-0 z-[24] h-[3px] rounded-full ${dropEdge === 'above' ? '-top-[2px]' : '-bottom-[1px]'}`}
          style={{ background: BLUE }} />
      )}
    </div>
  );
}

/** Is the point over one of the frames (iframes) inside `root`? */
function overFrame(root: Element, x: number, y: number): boolean {
  return [...root.querySelectorAll('iframe')].some((f) => {
    const r = f.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  });
}

export default function PageView({ content, theme, form, pageId, previewForm, formPreview, onFormDone, onBooked, preview, showThankYou, sections, merge, onCtaClick, onNextStep,
  editable, selectedId, onSelect, onDeselect, onMove, onMoveTo, onDuplicate, onDelete, onAddBelow, onEditText,
  canvasDragging, onCanvasDrop, onCanvasDragStart, stack, interactive, flashIds, flashKey, readOnly }: Props) {
  // Try it wins over edit: the canvas behaves like the live page, with no
  // chrome and no inline editors.
  const tryIt = interactive ?? null;
  const editing = !!editable && !tryIt;
  // Typography layer: when the page picks a font pairing, append the display-font
  // CSS var to heading + button classes, set --page-fd/--page-fb + the body font
  // on the page root, and render an inline <link> that loads the family in BOTH
  // the public page and the editor's iframe preview (children are portaled in).
  // No font set → wf is identity and the render is byte-identical to before.
  const fp = fontFor(theme.font);
  // A single-weight display face (fonts.ts `regular`) pins headings to its real
  // 400 weight (no browser-faked bold) and leaves buttons in the body face,
  // where a semibold label reads cleanly. Classic fonts are unchanged.
  const wf = (sc: StyleClasses): StyleClasses => fp
    ? fp.regular
      ? { ...sc, heading: `${sc.heading} [font-family:var(--page-fd)] !font-normal` }
      : { ...sc, heading: `${sc.heading} [font-family:var(--page-fd)]`, button: `${sc.button} [font-family:var(--page-fd)]` }
    : sc;
  const fontVars = fp
    ? ({ '--page-fd': fp.display, '--page-fb': fp.body, fontFamily: 'var(--page-fb)' } as React.CSSProperties)
    : undefined;
  const fontHead = fp ? <link rel="stylesheet" href={fp.href} /> : null;
  const s = wf(styleFor(theme.style));
  const brand = theme.brand_color || '#4f46e5';
  const editorial = theme.style === 'editorial';
  // Modern styles may color headings/labels/frames with the brand through
  // var(--page-brand). Classic pages keep their exact root style.
  const rootVars = s.roomy ? ({ ...fontVars, '--page-brand': brand, '--page-button': theme.button_color || brand } as React.CSSProperties) : fontVars;
  const norm = useMemo(
    () => sections ? { sections, thank_you: content.thank_you ?? {} } : normalizeContent(content, (n) => `s_${n}`),
    [sections, content],
  );

  // The footer (if any) renders at the bottom of BOTH the page and the
  // thank-you, unless it is hidden ("Hide from page").
  const footer = content.footer && !content.footer.hidden ? content.footer : undefined;
  const withFooter = (list: Section[]) => (footer ? [...list, footer] : list);
  const tySections = useMemo(() => thankYouSections(content.thank_you, (n) => `ty_${n}`), [content.thank_you]);
  // `list` is every section of this view, hidden ones included: the editor's
  // indices (moves, drops) refer to it. `shown` is what renders: a section with
  // hidden: true is kept in the page but never rendered, here, on the live
  // page, on the server and in every step and thank-you. Pure data, so the
  // server and the browser agree.
  const list = showThankYou ? tySections : norm.sections;
  // The edit canvas keeps a booking section without a calendar (it shows a
  // placeholder there); visitors, Try it and previews never see an empty band.
  const shown = useMemo(
    () => (editing ? (list.some((x) => x.hidden) ? list.filter((x) => !x.hidden) : list) : visitorSections(list)),
    [list, editing],
  );
  // Fill {{tokens}} from the handoff context (booking/form). No merge context →
  // sections render literally (so the editor preview shows the raw tokens).
  const display = useMemo(
    () => (merge ? withFooter(shown).map((sec) => mergeSection(sec, merge)) : withFooter(shown)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shown, footer, merge],
  );

  const glow = (
    <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
      style={{ background: `radial-gradient(60% 100% at 50% 0%, ${brand}26 0%, transparent 70%)` }} />
  );
  // Flat (modern) styles paint no ambient glow: the surface is the color.
  const atmosphere = editorial ? <><EditorialBackdrop /><EditorialMotif /></> : s.flat ? null : glow;

  // Popup form: the edit canvas always shows the form INLINE (so its fields
  // stay editable); Try it, previews and the public page honor theme.form_mode.
  // When 'popup', the inline Form section renders a trigger button and CTAs to
  // '#form' open this modal instead of scrolling.
  const effectiveFormMode: 'inline' | 'popup' = editing ? 'inline' : (theme.form_mode ?? 'inline');
  const [popupOpen, setPopupOpen] = useState(false);
  const openForm = () => setPopupOpen(true);
  useEffect(() => { if (showThankYou) setPopupOpen(false); }, [showThankYou]);
  // External openers (e.g. PublicLandingPage's last-step '#next' → form fallback)
  // dispatch OPEN_FORM_EVENT ('nla:open-form') on window to open the popup.
  useEffect(() => {
    if (effectiveFormMode !== 'popup') return;
    const onOpen = () => setPopupOpen(true);
    window.addEventListener(OPEN_FORM_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_FORM_EVENT, onOpen);
  }, [effectiveFormMode]);

  // ── Try it ──────────────────────────────────────────────────────────
  const tryOn = !!tryIt;
  const canvasRootRef = useRef<HTMLDivElement>(null);
  // Scroll inside the frame the page renders in (never the editor window).
  const scrollInFrame = (id: string) => {
    const doc = canvasRootRef.current?.ownerDocument;
    if (!doc) return;
    const view = doc.defaultView;
    const behavior: ScrollBehavior = view?.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    if (!id) { view?.scrollTo?.({ top: 0, behavior }); return; }
    const el = doc.getElementById(id);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior, block: 'start' });
  };
  const hasForm = list.some((x) => x.type === 'form');
  // '#next': the next step, or on the last step the form (the popup, or a
  // scroll to the inline form), exactly like the live page.
  const tryNext = () => {
    if (!tryIt) return;
    const last = tryIt.lastStep ?? !tryIt.onNextStep;
    if (!last && tryIt.onNextStep) { tryIt.onNextStep(); return; }
    if (!hasForm) return;
    if (effectiveFormMode === 'popup') setPopupOpen(true);
    else scrollInFrame('form');
  };
  // A form sent in Try it runs in preview mode: the answers are checked, nothing
  // is saved. The page moves on the way the live page would (see
  // PublicLandingPage), and the editor says that nothing was saved.
  const tryFormDone = (done: FormDone) => {
    if (!tryIt) return;
    tryIt.onNotice(TRY_IT_NOTICE.form);
    // Not a fit, a redirect, or a calendar to book: the form shows its own screen.
    if (!done.qualified || done.redirect || done.calendar) return;
    const action = afterSubmitAction(list.find((x) => x.type === 'form'), tryIt.lastStep ?? !tryIt.onNextStep);
    if (action === 'next' && tryIt.onNextStep) { tryIt.onNextStep(); return; }
    if (action === 'thank_you' && tryIt.onThankYou && !showThankYou) tryIt.onThankYou();
    // 'form': the form's own end screen stays.
  };
  // A call booked in Try it (a form's calendar) moves to the next step.
  const tryBooked = () => {
    if (!tryIt) return;
    const last = tryIt.lastStep ?? !tryIt.onNextStep;
    if (!last && tryIt.onNextStep) tryIt.onNextStep();
  };
  // Links: an in-page '#anchor' scrolls inside the frame; anything else (another
  // page, another site) stays put and says where links work. A click the page
  // already handled ('#next', a popup '#form') is left alone. A click on a
  // booking calendar or an embed says where those work.
  const tryClick = (e: React.MouseEvent) => {
    if (!tryIt) return;
    const t = e.target as Element;
    const a = t.closest?.('a[href]') as HTMLAnchorElement | null;
    if (a) {
      if (e.isDefaultPrevented() || e.nativeEvent.defaultPrevented) return;
      e.preventDefault();
      const href = a.getAttribute('href') ?? '';
      if (href.startsWith('#')) {
        let id = href.slice(1);
        try { id = decodeURIComponent(id); } catch { /* keep it as written */ }
        scrollInFrame(id);
        return;
      }
      tryIt.onNotice(TRY_IT_NOTICE.link);
      return;
    }
    const shield = t.closest?.('[data-try-embed]') as HTMLElement | null;
    if (shield && overFrame(shield, e.clientX, e.clientY)) {
      tryIt.onNotice(shield.getAttribute('data-try-embed') === 'booking' ? TRY_IT_NOTICE.booking : TRY_IT_NOTICE.embed);
    }
  };
  // No native submit ever leaves the frame (the form lives in its own frame;
  // this is the backstop).
  const trySubmitCapture = tryIt ? (e: React.FormEvent) => { e.preventDefault(); } : undefined;

  // What the sections get. Try it renders like the live page with every
  // outward call swapped for the local stand-ins above; the public page and the
  // edit canvas pass the caller's values through.
  const pv = tryIt ? false : preview;
  const formDone = tryIt ? tryFormDone : onFormDone;
  const booked = tryIt ? tryBooked : onBooked;
  const nextStep = tryIt ? tryNext : onNextStep;
  const ctaClick = tryIt ? undefined : onCtaClick;
  // The editor canvas (Edit and Try it) and read-only renders (thumbnails) open
  // the form in preview mode: no lead is saved and the form's views don't count.
  const fPreview = !!formPreview || editing || !!tryIt || !!preview;
  const formProps = { form, pageId, previewForm, formPreview: fPreview, onFormDone: formDone, onBooked: booked };

  const popupModal = effectiveFormMode === 'popup'
    ? <FormModal open={popupOpen} onClose={() => setPopupOpen(false)} {...formProps}
        fieldDesign={shown.find((section) => section.type === 'form')?.form_fields} theme={theme} s={s} preview={pv} />
    : null;

  // Inline text editing is armed for EVERY section in edit mode, so one click
  // on any text starts typing there. Everywhere else (every public page, Try
  // it) SectionRenderer gets no onEditText and renders raw strings exactly as
  // before.
  const editTextFor = (section: Section) =>
    editing && onEditText
      ? (patch: Partial<Section>) => onEditText(section.id, patch)
      : undefined;

  // Canvas drag-and-drop. React synthetic events DO reach the iframe canvas (same
  // as click — verified), so DnD is wired with ordinary React handlers on the
  // section wrappers (NOT native iframe-document listeners). The parent owns the
  // drag payload (add a new component vs. reorder an existing one) and performs the
  // mutation in onCanvasDrop(beforeId); `dropAt` previews the insertion line.
  const [dropAt, setDropAt] = useState<{ id: string; below: boolean } | null>(null);
  // The section id to insert BEFORE for a drop on `id`'s lower/upper half (null = end).
  const beforeIdFor = (id: string, below: boolean): string | null => {
    const i = list.findIndex((x) => x.id === id);
    if (i < 0) return null;
    return below ? (list[i + 1]?.id ?? null) : list[i].id;
  };
  // Clear the drop line whenever a drag session ends.
  useEffect(() => { if (!canvasDragging) setDropAt(null); }, [canvasDragging]);
  const dropOn = editing && !!canvasDragging;

  // Switching from Try it back to Edit remounts the section wrappers with the
  // selection still on: that must not scroll the canvas.
  const wasTry = useRef(tryOn);
  const enteringEdit = wasTry.current && !tryOn;
  useEffect(() => { wasTry.current = tryOn; });

  // Editable canvas: a click on the page background (anywhere outside a
  // section) clears the selection. A click inside a section never gets here
  // (EditableSection stops it); toolbar clicks do, so they are skipped by the
  // check. Public pages get no handler at all.
  const bgClick = editing && onDeselect
    ? (e: React.MouseEvent) => { if (!(e.target as Element).closest?.('[data-section-id]')) onDeselect(); }
    : undefined;
  const rootClick = editing ? bgClick : tryIt ? tryClick : undefined;
  // The edit canvas clips sideways only: an overflow-hidden root would trap the
  // sticky section toolbar and clip the "+" under the last section.
  const rootClip = editing ? 'flow-root [overflow-x:clip]' : 'overflow-hidden';

  // The sections that render as their own boxes.
  const rendered = shown;
  // Up / Down swap with the nearest VISIBLE neighbor, stepping over hidden ones.
  const moveBy = (id: string, dir: -1 | 1) => {
    const vi = rendered.findIndex((x) => x.id === id);
    const neighbor = vi >= 0 ? rendered[vi + dir] : undefined;
    if (!neighbor) return;
    const from = list.findIndex((x) => x.id === id);
    const to = list.findIndex((x) => x.id === neighbor.id);
    if (onMoveTo && Math.abs(to - from) > 1) onMoveTo(id, to);
    else onMove?.(id, dir);
  };

  // The edit layer around one section: the section's index among the rendered
  // sections drives the move bounds; the appended footer (not in `list`) is
  // selectable and removable but not movable or duplicable.
  const editWrap = (section: Section, node: React.ReactNode, key?: React.Key) => {
    const isFooter = !list.some((x) => x.id === section.id);
    const vi = rendered.findIndex((x) => x.id === section.id);
    return (
      <EditableSection key={key} id={section.id} label={SECTION_LABEL[section.type] ?? 'Section'} selected={selectedId === section.id}
        isFooter={isFooter} canUp={vi > 0} canDown={vi >= 0 && vi < rendered.length - 1}
        skipInitialScroll={enteringEdit}
        flashKey={flashIds?.includes(section.id) ? flashKey ?? 0 : undefined}
        dropEdge={dropAt && dropAt.id === section.id ? (dropAt.below ? 'below' : 'above') : null}
        draggable={!!onCanvasDrop && !isFooter && !readOnly}
        readOnly={readOnly}
        dndActive={dropOn}
        onDragStartSelf={() => onCanvasDragStart?.(section.id)}
        onDragOverSelf={(below) => setDropAt({ id: section.id, below })}
        onDropSelf={(below) => { setDropAt(null); onCanvasDrop?.(beforeIdFor(section.id, below)); }}
        onSelect={onSelect} onMoveBy={(dir) => moveBy(section.id, dir)} onDuplicate={onDuplicate} onDelete={onDelete} onAddBelow={onAddBelow}>
        {node}
      </EditableSection>
    );
  };

  // A section's body inside its band. The public page keeps its Reveal scroll
  // fade (`immediate` for the top bar, the announcement strip and the first
  // real section, which are above the fold). The canvas renders sections at
  // once in a plain box of the same shape; in Try it a booking calendar or an
  // embed sits behind a shield (no booking, nothing sent).
  const sectionBody = (section: Section, node: React.ReactNode, immediate?: boolean) => {
    if (tryIt && (section.type === 'booking' || section.type === 'embed')) {
      return <div data-try-embed={section.type} className="[&_iframe]:pointer-events-none">{node}</div>;
    }
    if (tryIt || editing) return <div>{node}</div>;
    return <Reveal immediate={immediate} animation={section.animation}>{node}</Reveal>;
  };

  // When any section opts into a background band, render full-width bands (each in
  // its own tone with matching text colors) so the page can alternate light/dark.
  // Modern (roomy) styles and the top bar / announcement strip always band.
  // Otherwise keep the original single centered container (zero change for old pages).
  // The top bar / announcement strip and the first real section paint at once
  // (no scroll-fade): they are above the fold. With neither present this is
  // index 0, exactly the old "first section is immediate" rule.
  const firstContent = Math.max(0, display.findIndex((sec) => sec.type !== 'nav' && sec.type !== 'banner'));
  const anyBands = !!s.roomy || display.some((sec) => (sec.bg && sec.bg !== 'default') || sec.bg_image_url || sec.type === 'nav' || sec.type === 'banner');
  if (anyBands) {
    return (
      <div ref={canvasRootRef} className={`relative ${rootClip} ${s.page}`} style={rootVars} onClick={rootClick} onSubmitCapture={trySubmitCapture}
        onDragOver={dropOn ? (e) => e.preventDefault() : undefined}
        onDrop={dropOn && onCanvasDrop ? (e) => { e.preventDefault(); setDropAt(null); onCanvasDrop(null); } : undefined}>
        {fontHead}
        {atmosphere}
        <div className="relative">
          {display.map((section, i) => {
            const hasBgImg = !!section.bg_image_url;
            const design = normalizeSectionDesign(section.design);
            const designed = sectionDesignPresentation(design, stack);
            const contentStyle = design?.content_width ? { maxWidth: SECTION_MAX_WIDTH[design.content_width] } : undefined;
            // The announcement strip defaults to a brand-color fill.
            const bgKey = section.type === 'banner' && (!section.bg || section.bg === 'default') ? 'brand' : section.bg;
            // Modern tones resolve against the page style (flat surface + inline color).
            const modern = !hasBgImg && isModernBg(bgKey) ? bandTone(s, bgKey, brand) : null;
            const banded = !modern && !!bgKey && bgKey !== 'default' && !isModernBg(bgKey);
            // A background image forces the dark tone (white text over the scrim);
            // otherwise a band uses its own tone, and a plain section the page tone.
            // bs.band = the tone's bg+text WITHOUT min-h-screen — a band must size
            // to its content, not swallow a full viewport (dark-on-dark bug).
            const bs = hasBgImg ? (s.roomy ? wf({ ...(s.inkTone ?? { ...INK_TONE, button: s.button }), ...darkMark(s) }) : wf(styleFor('dark')))
              : modern ? wf(modern.s) : banded ? wf(styleFor(bgKey as PageStyle)) : s;
            const bandStyle = hasBgImg
              ? { backgroundImage: `url("${section.bg_image_url}")`, backgroundSize: 'cover', backgroundPosition: design?.background_position ?? 'center' }
              : modern ? modern.css : undefined;
            // Avatar stacks cut their ring out of the band surface (modern only, so
            // classic band markup is untouched).
            const vars = (s.roomy || modern)
              ? ({ '--band-bg': (modern?.css.background as string | undefined) ?? s.surface ?? '#ffffff' } as React.CSSProperties)
              : undefined;
            const surfaceStyle = design ? { ...bandStyle, ...vars, ...designed.style, ...(design.background_color && !hasBgImg ? { backgroundImage: 'none' } : {}) }
              : vars ? { ...bandStyle, ...vars } : bandStyle;
            const pad = section.type === 'banner' ? 'py-2.5'
              : section.type === 'nav' ? 'py-3.5 sm:py-4'
              : bandPad(section.space, section.space_below, s.roomy);
            const body = sectionBody(section,
              <SectionRenderer section={section} s={bs} theme={theme} {...formProps}
                preview={pv} onCtaClick={ctaClick} onNextStep={nextStep}
                onEditText={editTextFor(section)} formMode={effectiveFormMode} onOpenForm={openForm} editable={editing || undefined} stack={stack}
                accent={modern?.accent ?? (hasBgImg && s.roomy && luminance(brand) < 0.16 ? '#ffffff' : undefined)} />,
              i <= firstContent,
            );
            // Inset panel (modern styles): the tone paints a rounded card inside
            // the page margins instead of a full-bleed band.
            const band = section.inset && s.roomy && (modern || hasBgImg) && section.type !== 'banner' && section.type !== 'nav' ? (
              <div key={section.id} id={section.anchor || undefined}
                className={`relative px-3 sm:px-4 ${bandPad(section.space ?? 'tight', section.space_below ?? 'tight', true)}${section.anchor ? ' scroll-mt-6' : ''}`}>
                <div className={`relative mx-auto max-w-6xl overflow-hidden rounded-[24px] px-5 py-12 sm:rounded-[28px] sm:px-12 sm:py-16 ${hasBgImg ? '' : bs.band}${designed.className}`}
                  style={design?.content_width ? { ...surfaceStyle, ...contentStyle } : surfaceStyle}>
                  {hasBgImg && <div aria-hidden className="absolute inset-0" style={{ background: `rgba(0,0,0,${(section.bg_overlay ?? 45) / 100})` }} />}
                  <div className="relative">{body}</div>
                </div>
              </div>
            ) : (
              <div key={section.id} id={section.anchor || undefined}
                className={`relative ${hasBgImg ? '' : banded || modern ? bs.band : ''} px-4 ${pad}${section.anchor ? ' scroll-mt-6' : ''}${designed.className}`}
                style={surfaceStyle}>
                {hasBgImg && <div aria-hidden className="absolute inset-0" style={{ background: `rgba(0,0,0,${(section.bg_overlay ?? 45) / 100})` }} />}
                <div className="relative mx-auto max-w-6xl" style={contentStyle}>{body}</div>
              </div>
            );
            // The edit layer hugs the whole band, edge to edge, like the
            // section a visitor sees.
            return editing ? editWrap(section, band, section.id) : band;
          })}
        </div>
        {popupModal}
      </div>
    );
  }

  const classicDesign = display.some((section) => !!normalizeSectionDesign(section.design));
  // One styled wrapper for both views, so the thank-you carries the exact same
  // style preset, brand glow, container rhythm, and scroll reveal as the page.
  return (
    <div ref={canvasRootRef} className={`relative ${rootClip} ${s.page} px-4 py-14 sm:py-20`} style={fontVars} onClick={rootClick} onSubmitCapture={trySubmitCapture}
      onDragOver={dropOn ? (e) => e.preventDefault() : undefined}
      onDrop={dropOn && onCanvasDrop ? (e) => { e.preventDefault(); setDropAt(null); onCanvasDrop(null); } : undefined}>
      {fontHead}
      {atmosphere}
      <div className={classicDesign ? 'relative grid w-full' : 'relative mx-auto grid max-w-6xl'}>
        {display.map((section, i) => {
          const node = (
            <SectionRenderer section={section} s={s} theme={theme} {...formProps}
              preview={pv} onCtaClick={ctaClick} onNextStep={nextStep}
              onEditText={editTextFor(section)} formMode={effectiveFormMode} onOpenForm={openForm} editable={editing || undefined} stack={stack} />
          );
          const design = normalizeSectionDesign(section.design);
          const designed = sectionDesignPresentation(design, stack);
          const surface = design ? (
            <div className={`lp-design-classic px-4${designed.className}`} style={designed.style}>
              <div className="mx-auto max-w-6xl" style={design.content_width ? { maxWidth: SECTION_MAX_WIDTH[design.content_width] } : undefined}>{node}</div>
            </div>
          ) : node;
          return (
            <div key={section.id} className={sectionGap(i === 0 ? null : display[i - 1].type, section.type, section.space, i === 0 ? undefined : display[i - 1].space_below)}
              style={classicDesign && !design ? { maxWidth: '72rem', width: '100%', marginLeft: 'auto', marginRight: 'auto' } : undefined}>
              {editing ? editWrap(section, surface) : sectionBody(section, surface, i === 0)}
            </div>
          );
        })}
      </div>
      {popupModal}
    </div>
  );
}
